import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  Button,
  Select,
  FormControl,
  InputLabel,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar,
  useTheme,
  Alert,
  Snackbar,
  Fab,
  Tooltip,
  InputAdornment,
  Badge,
  Menu,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  Star as StarIcon,
  Refresh as RefreshIcon,
  FilterList as FilterIcon,
  Notes as NotesIcon,
  Visibility as VisibilityIcon,
  MoreVert as MoreVertIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import CustomerForm from './CustomerForm';
import { makeApiCall } from '../../config/api';

const CRMDashboard = ({ onNavigateToCustomer }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { user } = useAuth();
  
  // State for customers and filters
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCustomers, setTotalCustomers] = useState(0);
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [customerType, setCustomerType] = useState('');
  const [salesPersonId, setSalesPersonId] = useState('');
  const [isSignedClient, setIsSignedClient] = useState('');
  const [showActiveOnly, setShowActiveOnly] = useState(true);
  // Quick-filter from the "New This Month" KPI card. Matches the 30-day
  // window used by the stats endpoint so the count stays consistent.
  const NEW_THIS_MONTH_DAYS = 30;
  const [createdWithinDays, setCreatedWithinDays] = useState('');
  
  // Sorting
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('DESC');
  
  // Dialog states
  const [openAddCustomer, setOpenAddCustomer] = useState(false);
  const [openEditCustomer, setOpenEditCustomer] = useState(false);
  const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  
  // Menu state for actions
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuCustomer, setMenuCustomer] = useState(null);
  
  // Stats
  const [stats, setStats] = useState({});
  
  // Lookup data
  const [lookups, setLookups] = useState({
    sales_persons: [],
    lead_sources: [],
    property_types: [],
    customer_groups: []
  });

  // Handle menu open/close
  const handleMenuOpen = (event, customer) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setMenuCustomer(customer);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuCustomer(null);
  };

  const handleMenuAction = (action) => {
    handleMenuClose();
    
    switch (action) {
      case 'view':
        handleCustomerClick(menuCustomer);
        break;
      case 'edit':
        setSelectedCustomer(menuCustomer);
        setOpenEditCustomer(true);
        break;
      case 'delete':
        setSelectedCustomer(menuCustomer);
        setOpenDeleteConfirm(true);
        break;
      default:
        break;
    }
  };

  // Handle column sorting
  const handleSort = (column) => {
    if (sortBy === column) {
      // Toggle sort order if same column
      setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC');
    } else {
      // New column, default to DESC for dates, ASC for names
      setSortBy(column);
      setSortOrder(column === 'created_at' ? 'DESC' : 'ASC');
    }
  };

  // Fetch customers
  const fetchCustomers = async () => {
    try {
      setLoading(true);
      
      const params = new URLSearchParams({
        page: page + 1,
        limit: rowsPerPage,
        search: searchTerm,
        customer_type: customerType,
        sales_person_id: salesPersonId,
        is_signed_client: isSignedClient,
        is_active: showActiveOnly.toString(),
        sort_by: sortBy,
        sort_order: sortOrder
      });

      if (createdWithinDays) {
        params.set('created_within_days', String(createdWithinDays));
      }

      const response = await makeApiCall(`api/customers?${params}`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setCustomers(data.customers);
      setTotalCustomers(data.pagination.total);

    } catch (error) {
      console.error('Error fetching customers:', error);
      setError('Failed to load customers');
    } finally {
      setLoading(false);
    }
  };

  // Fetch stats
  const fetchStats = async () => {
    try {
      const response = await makeApiCall('api/customers/stats/overview');

      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  // Fetch lookup data
  const fetchLookups = async () => {
    try {
      const response = await makeApiCall('api/customers/lookups/all');

      if (response.ok) {
        const data = await response.json();
        setLookups(data);
      }
    } catch (error) {
      console.error('Error fetching lookups:', error);
    }
  };

  // Delete customer
  const handleDeleteCustomer = async () => {
    try {
      const response = await makeApiCall(`api/customers/${selectedCustomer.id}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        setSuccess('Customer deleted successfully');
        fetchCustomers();
        setOpenDeleteConfirm(false);
        setSelectedCustomer(null);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to delete customer');
      }
    } catch (error) {
      console.error('Error deleting customer:', error);
      setError('Failed to delete customer');
    }
  };

  // Handle form success
  const handleFormSuccess = (message) => {
    setSuccess(message);
    fetchCustomers();
    fetchStats();
    setOpenAddCustomer(false);
    setOpenEditCustomer(false);
    setSelectedCustomer(null);
  };

  // Handle customer row click
  const handleCustomerClick = (customer) => {
    if (onNavigateToCustomer) {
      onNavigateToCustomer(customer.id);
    }
  };

  // Get customer type color
  const getCustomerTypeColor = (type) => {
    switch (type) {
      case 'residential': return 'primary';
      case 'multi_residential': return 'info';
      case 'commercial': return 'secondary';
      case 'construction': return 'warning';
      default: return 'default';
    }
  };

  // TKT-184: friendly labels for the customer-type enum so the chips
  // don't render raw values like "multi_residential".
  const getCustomerTypeLabel = (type) => {
    switch (type) {
      case 'residential':
        return t('crm.customerTypes.residential', 'Residential');
      case 'multi_residential':
        return t('crm.customerTypes.multiResidential', 'Multi-Residential');
      case 'commercial':
        return t('crm.customerTypes.commercialIndustrial', 'Commercial / Industrial');
      case 'construction':
        return t('crm.customerTypes.construction', 'Construction');
      default:
        return type;
    }
  };

  // Get status color
  const getStatusColor = (customer) => {
    if (!customer.is_active) return 'error';
    if (customer.is_signed_client) return 'success';
    if (customer.quoted) return 'info';
    return 'default';
  };

  // Get status text
  const getStatusText = (customer) => {
    if (!customer.is_active) return t('crm.inactive');
    if (customer.is_signed_client) return t('crm.signed');
    if (customer.quoted) return t('crm.quoted');
    return t('common.active');
  };

  // Create sortable table header
  const SortableTableCell = ({ column, children, ...props }) => {
    const isActive = sortBy === column;
    const isAscending = sortOrder === 'ASC';
    
    return (
      <TableCell
        {...props}
        sx={{
          cursor: 'pointer',
          userSelect: 'none',
          '&:hover': {
            backgroundColor: theme.palette.action.hover
          },
          fontWeight: isActive ? 'bold' : 'normal',
          ...props.sx
        }}
        onClick={() => handleSort(column)}
      >
        <Box display="flex" alignItems="center">
          {children}
          {isActive && (
            <Box ml={1}>
              {isAscending ? <ArrowUpwardIcon sx={{ fontSize: 16 }} /> : <ArrowDownwardIcon sx={{ fontSize: 16 }} />}
            </Box>
          )}
        </Box>
      </TableCell>
    );
  };

  // Use effect to fetch data
  useEffect(() => {
    fetchCustomers();
  }, [page, rowsPerPage, searchTerm, customerType, salesPersonId, isSignedClient, showActiveOnly, sortBy, sortOrder, createdWithinDays]);

  useEffect(() => {
    fetchStats();
    fetchLookups();
  }, []);

  // Toggle the "New This Month" drill-down from the KPI card. When the
  // filter turns on we reset to page 0 so the user sees the first results
  // and we collapse the activity to that subset only (the chip in the
  // filter row lets them clear it).
  const toggleNewThisMonthFilter = () => {
    setPage(0);
    setCreatedWithinDays((current) => (current ? '' : NEW_THIS_MONTH_DAYS));
  };

  const newThisMonthActive = createdWithinDays === NEW_THIS_MONTH_DAYS;

  // Stats cards data. Cards may declare `onClick`, `active`, and an
  // accessible label so the renderer can promote them to real buttons.
  const statsCards = [
    {
      title: t('crm.totalCustomers'),
      value: stats.total_customers || 0,
      icon: <PersonIcon />,
      color: 'primary.main'
    },
    {
      title: t('crm.activeCustomers'),
      value: stats.active_customers || 0,
      icon: <PersonIcon />,
      color: 'success.main'
    },
    {
      title: t('crm.signedClients'),
      value: stats.signed_clients || 0,
      icon: <StarIcon />,
      color: 'warning.main'
    },
    {
      title: t('crm.newThisMonth'),
      value: stats.new_this_month || 0,
      icon: <AddIcon />,
      color: 'info.main',
      onClick: toggleNewThisMonthFilter,
      active: newThisMonthActive,
      ariaLabel: newThisMonthActive
        ? t('crm.newThisMonthClear', 'Clear New This Month filter')
        : t('crm.newThisMonthFilter', 'Show only customers added in the last 30 days'),
    }
  ];

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }} data-demo-target="page-customers-root">
      {/* Header */}
      <Box 
        display="flex" 
        justifyContent="space-between" 
        alignItems="center" 
        flexDirection={{ xs: 'column', sm: 'row' }}
        gap={{ xs: 2, sm: 0 }}
        data-demo-target="page-customers-header"
      >
        <Typography 
          variant="h4" 
          component="h1" 
          sx={{ 
            fontWeight: 'bold',
            color: '#000062',
            fontSize: { xs: '1.5rem', md: '2.125rem' },
            mb: 3
          }}
        >
          {t('navigation.customers')}
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setOpenAddCustomer(true)}
          sx={{ 
            bgcolor: theme.palette.primary.main,
            minWidth: { xs: '100%', sm: 'auto' }
          }}
        >
          {t('crm.createCustomer')}
        </Button>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={{ xs: 2, md: 3 }} mb={{ xs: 2, md: 3 }}>
        {statsCards.map((stat, index) => {
          const interactive = typeof stat.onClick === 'function';
          const cardSx = {
            height: '100%',
            transition: 'box-shadow 120ms ease, transform 120ms ease, border-color 120ms ease',
            border: '2px solid',
            borderColor: stat.active ? 'info.main' : 'transparent',
            ...(interactive ? {
              cursor: 'pointer',
              '&:hover': {
                boxShadow: 4,
                transform: 'translateY(-1px)',
              },
              '&:focus-visible': {
                outline: '2px solid',
                outlineColor: 'info.main',
                outlineOffset: 2,
              },
            } : {}),
          };
          return (
            <Grid size={{ xs: 6, sm: 6, md: 3 }} key={index}>
              <Card
                sx={cardSx}
                {...(interactive
                  ? {
                      role: 'button',
                      tabIndex: 0,
                      'aria-pressed': !!stat.active,
                      'aria-label': stat.ariaLabel || stat.title,
                      onClick: stat.onClick,
                      onKeyDown: (e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          stat.onClick();
                        }
                      },
                    }
                  : {})}
              >
                <CardContent sx={{ p: { xs: 1.5, md: 2 }, '&:last-child': { pb: { xs: 1.5, md: 2 } } }}>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography 
                        color="textSecondary" 
                        gutterBottom 
                        variant="body2"
                        sx={{ fontSize: { xs: '0.7rem', md: '0.875rem' } }}
                      >
                        {stat.title}
                      </Typography>
                      <Typography 
                        variant="h4" 
                        component="h2"
                        sx={{ fontSize: { xs: '1.5rem', md: '2.125rem' } }}
                      >
                        {stat.value}
                      </Typography>
                    </Box>
                    <Avatar sx={{ 
                      bgcolor: stat.color, 
                      width: { xs: 40, md: 56 }, 
                      height: { xs: 40, md: 56 }
                    }}>
                      {stat.icon}
                    </Avatar>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* Filters */}
      <Card sx={{ mb: { xs: 2, md: 3 } }}>
        <CardContent sx={{ p: { xs: 1.5, md: 2 } }}>
          <Grid container spacing={{ xs: 1.5, md: 2 }} alignItems="center">
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                label={t('crm.searchCustomers')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid size={{ xs: 6, sm: 4, md: 2.4 }}>
              <FormControl fullWidth size="small" sx={{ minWidth: 150 }}>
                <InputLabel>{t('crm.customerType')}</InputLabel>
                <Select
                  value={customerType}
                  label={t('crm.customerType')}
                  onChange={(e) => setCustomerType(e.target.value)}
                >
                  <MenuItem value="">{t('common.all')} {t('quotes.type')}</MenuItem>
                  <MenuItem value="residential">{t('crm.customerTypes.residential', 'Residential')}</MenuItem>
                  <MenuItem value="multi_residential">{t('crm.customerTypes.multiResidential', 'Multi-Residential')}</MenuItem>
                  <MenuItem value="commercial">{t('crm.customerTypes.commercialIndustrial', 'Commercial / Industrial')}</MenuItem>
                  <MenuItem value="construction">{t('crm.customerTypes.construction', 'Construction')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 6, sm: 4, md: 2.4 }}>
              <FormControl fullWidth size="small" sx={{ minWidth: 150 }}>
                <InputLabel>{t('crm.salesPerson')}</InputLabel>
                <Select
                  value={salesPersonId}
                  label={t('crm.salesPerson')}
                  onChange={(e) => setSalesPersonId(e.target.value)}
                >
                  <MenuItem value="">{t('common.all')} {t('crm.salesPerson')}</MenuItem>
                  {lookups.sales_persons.map((sp) => (
                    <MenuItem key={sp.id} value={sp.id}>
                      {sp.display_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 6, sm: 4, md: 2.4 }}>
              <FormControl fullWidth size="small" sx={{ minWidth: 150 }}>
                <InputLabel>{t('common.status')}</InputLabel>
                <Select
                  value={isSignedClient}
                  label={t('common.status')}
                  onChange={(e) => setIsSignedClient(e.target.value)}
                >
                  <MenuItem value="">{t('common.all')} {t('common.status')}</MenuItem>
                  <MenuItem value="true">{t('crm.signed')}</MenuItem>
                  <MenuItem value="false">No {t('crm.signed')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 6, sm: 4, md: 2.4 }}>
              <FormControl fullWidth size="small" sx={{ minWidth: 150 }}>
                <InputLabel>{t('crm.show')}</InputLabel>
                <Select
                  value={showActiveOnly.toString()}
                  label={t('crm.show')}
                  onChange={(e) => setShowActiveOnly(e.target.value === 'true')}
                >
                  <MenuItem value="true">{t('common.active')} {t('crm.customers')}</MenuItem>
                  <MenuItem value="false">{t('common.all')} {t('crm.customers')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 1.6 }}>
              <Box display="flex" alignItems="center" gap={1} justifyContent={{ xs: 'center', md: 'flex-start' }}>
                <Tooltip title={t('crm.refresh')}>
                  <IconButton 
                    onClick={fetchCustomers} 
                    color="primary" 
                    size="medium"
                    sx={{ p: { xs: 1, md: 1.5 } }}
                  >
                    <RefreshIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            </Grid>
            {newThisMonthActive && (
              <Grid size={{ xs: 12 }}>
                <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                  <Typography variant="body2" color="text.secondary">
                    {t('crm.activeFilters', 'Active filters:')}
                  </Typography>
                  <Chip
                    label={t('crm.newThisMonth')}
                    color="info"
                    size="small"
                    onDelete={toggleNewThisMonthFilter}
                    aria-label={t('crm.newThisMonthClear', 'Clear New This Month filter')}
                  />
                </Box>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>

      {/* Customers Table */}
      <Card>
        <CardContent sx={{ p: { xs: 0, md: 2 } }}>
          {/* Desktop Table View */}
          <Box sx={{ display: { xs: 'none', md: 'block' } }}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <SortableTableCell column="last_name">Customer</SortableTableCell>
                    <TableCell>Contact</TableCell>
                    <TableCell>Location</TableCell>
                    <SortableTableCell column="customer_type">Type</SortableTableCell>
                    <TableCell>Sales Person</TableCell>
                    <TableCell>Notes</TableCell>
                    <TableCell>Status</TableCell>
                    <SortableTableCell column="created_at">Created</SortableTableCell>
                    <TableCell align="center" width={80}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      <Typography>Loading customers...</Typography>
                    </TableCell>
                  </TableRow>
                ) : customers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      <Typography>No customers found</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  customers.map((customer, idx) => (
                    <TableRow 
                      key={customer.id} 
                      hover
                      data-demo-target={idx === 0 ? 'page-customers-row-first' : undefined}
                      sx={{ 
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: theme.palette.action.hover
                        }
                      }}
                      onClick={() => handleCustomerClick(customer)}
                    >
                      <TableCell>
                        <Box display="flex" alignItems="center">
                          <Avatar sx={{ mr: 2, bgcolor: theme.palette.primary.main }}>
                            {customer.company_name ? <BusinessIcon /> : <PersonIcon />}
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle2">
                              {customer.full_name || `${customer.first_name || ''} ${customer.last_name || ''}`.trim()}
                            </Typography>
                            {customer.company_name && (
                              <Typography variant="body2" color="textSecondary">
                                {customer.company_name}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box>
                          {customer.email_address && (
                            <Box display="flex" alignItems="center" mb={0.5}>
                              <EmailIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                              <Typography variant="body2">{customer.email_address}</Typography>
                            </Box>
                          )}
                          {customer.phone_number && (
                            <Box display="flex" alignItems="center">
                              <PhoneIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                              <Typography variant="body2">{customer.phone_number}</Typography>
                            </Box>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="flex-start">
                          <LocationIcon sx={{ fontSize: 16, mr: 1, mt: 0.3, color: 'text.secondary' }} />
                          <Box>
                            {customer.street_address && (
                              <Typography variant="body2">
                                {customer.street_address}
                              </Typography>
                            )}
                            <Typography variant="body2" color="textSecondary">
                              {[customer.city, customer.province, customer.postal_code].filter(Boolean).join(', ')}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getCustomerTypeLabel(customer.customer_type)}
                          color={getCustomerTypeColor(customer.customer_type)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {customer.sales_person_name || t('tasks.unassigned')}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center">
                          <Badge 
                            badgeContent={customer.notes_count || 0} 
                            color="primary"
                            showZero={false}
                          >
                            <NotesIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                          </Badge>
                          {customer.latest_note && (
                            <Typography 
                              variant="caption" 
                              color="textSecondary" 
                              sx={{ ml: 1, maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                            >
                              {customer.latest_note}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getStatusText(customer)}
                          color={getStatusColor(customer)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {new Date(customer.created_at).toLocaleDateString()}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title={t('crm.moreActions')}>
                          <IconButton
                            size="small"
                            onClick={(e) => handleMenuOpen(e, customer)}
                          >
                            <MoreVertIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          </Box>

          {/* Mobile Card View */}
          <Box sx={{ display: { xs: 'block', md: 'none' } }}>
            {loading ? (
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography>Loading customers...</Typography>
              </Box>
            ) : customers.length === 0 ? (
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography>No customers found</Typography>
              </Box>
            ) : (
              <Box sx={{ p: 1 }}>
                {customers.map((customer) => (
                  <Card 
                    key={customer.id} 
                    sx={{ 
                      mb: 1, 
                      cursor: 'pointer',
                      '&:hover': {
                        boxShadow: 3
                      }
                    }}
                    onClick={() => handleCustomerClick(customer)}
                  >
                    <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                        <Box display="flex" alignItems="center" flex={1}>
                          <Avatar sx={{ mr: 1.5, bgcolor: theme.palette.primary.main, width: 32, height: 32 }}>
                            {customer.company_name ? <BusinessIcon fontSize="small" /> : <PersonIcon fontSize="small" />}
                          </Avatar>
                          <Box flex={1}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', fontSize: '0.875rem' }}>
                              {customer.full_name || `${customer.first_name || ''} ${customer.last_name || ''}`.trim()}
                            </Typography>
                            {customer.company_name && (
                              <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.75rem' }}>
                                {customer.company_name}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Chip
                            label={getStatusText(customer)}
                            color={getStatusColor(customer)}
                            size="small"
                            sx={{ fontSize: '0.7rem', height: '20px' }}
                          />
                          <IconButton
                            size="small"
                            onClick={(e) => handleMenuOpen(e, customer)}
                          >
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </Box>
                      
                      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mb: 1 }}>
                        <Box>
                          {customer.email_address && (
                            <Box display="flex" alignItems="center" mb={0.5}>
                              <EmailIcon sx={{ fontSize: 14, mr: 0.5, color: 'text.secondary' }} />
                              <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                                {customer.email_address.length > 20 ? `${customer.email_address.substring(0, 20)}...` : customer.email_address}
                              </Typography>
                            </Box>
                          )}
                          {customer.phone_number && (
                            <Box display="flex" alignItems="center">
                              <PhoneIcon sx={{ fontSize: 14, mr: 0.5, color: 'text.secondary' }} />
                              <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>{customer.phone_number}</Typography>
                            </Box>
                          )}
                        </Box>
                        <Box>
                          <Box display="flex" alignItems="flex-start" mb={0.5}>
                            <LocationIcon sx={{ fontSize: 14, mr: 0.5, mt: 0.2, color: 'text.secondary' }} />
                            <Box>
                              {customer.street_address && (
                                <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                                  {customer.street_address}
                                </Typography>
                              )}
                              <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.75rem' }}>
                                {[customer.city, customer.province].filter(Boolean).join(', ')}
                              </Typography>
                            </Box>
                          </Box>
                          <Chip
                            label={getCustomerTypeLabel(customer.customer_type)}
                            color={getCustomerTypeColor(customer.customer_type)}
                            size="small"
                            sx={{ fontSize: '0.7rem', height: '20px' }}
                          />
                        </Box>
                      </Box>
                      
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.7rem' }}>
                          {customer.sales_person_name || t('tasks.unassigned')} • {new Date(customer.created_at).toLocaleDateString()}
                        </Typography>
                        {customer.notes_count > 0 && (
                          <Badge badgeContent={customer.notes_count} color="primary" sx={{ '& .MuiBadge-badge': { fontSize: '0.6rem' } }}>
                            <NotesIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                          </Badge>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            )}
          </Box>
          
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={totalCustomers}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(event, newPage) => setPage(newPage)}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(parseInt(event.target.value, 10));
              setPage(0);
            }}
            sx={{ mt: { xs: 1, md: 0 } }}
          />
        </CardContent>
      </Card>

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={() => handleMenuAction('view')}>
          <ListItemIcon>
            <VisibilityIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>View Details</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleMenuAction('edit')}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit Customer</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleMenuAction('delete')}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Delete Customer</ListItemText>
        </MenuItem>
      </Menu>

      {/* Add Customer Dialog */}
      <Dialog 
        open={openAddCustomer} 
        onClose={() => setOpenAddCustomer(false)} 
        maxWidth="md" 
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            m: { xs: 1, sm: 2 },
            height: { xs: 'calc(100% - 16px)', sm: 'auto' },
            maxHeight: { xs: 'calc(100% - 16px)', sm: '90vh' }
          }
        }}
      >
        <DialogTitle sx={{ fontSize: { xs: '1.25rem', md: '1.5rem' } }}>Add New Customer</DialogTitle>
        <DialogContent sx={{ p: { xs: 1, md: 2 } }}>
          <CustomerForm
            onSuccess={handleFormSuccess}
            onCancel={() => setOpenAddCustomer(false)}
            lookups={lookups}
          />
        </DialogContent>
      </Dialog>

      {/* Edit Customer Dialog */}
      <Dialog 
        open={openEditCustomer} 
        onClose={() => setOpenEditCustomer(false)} 
        maxWidth="md" 
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            m: { xs: 1, sm: 2 },
            height: { xs: 'calc(100% - 16px)', sm: 'auto' },
            maxHeight: { xs: 'calc(100% - 16px)', sm: '90vh' }
          }
        }}
      >
        <DialogTitle sx={{ fontSize: { xs: '1.25rem', md: '1.5rem' } }}>Edit Customer</DialogTitle>
        <DialogContent sx={{ p: { xs: 1, md: 2 } }}>
          {selectedCustomer && (
            <CustomerForm
              customer={selectedCustomer}
              onSuccess={handleFormSuccess}
              onCancel={() => setOpenEditCustomer(false)}
              lookups={lookups}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={openDeleteConfirm} onClose={() => setOpenDeleteConfirm(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete customer "{selectedCustomer?.full_name || `${selectedCustomer?.first_name || ''} ${selectedCustomer?.last_name || ''}`.trim()}"?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteConfirm(false)}>Cancel</Button>
          <Button onClick={handleDeleteCustomer} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success/Error Messages */}
      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={() => setSuccess('')}
      >
        <Alert onClose={() => setSuccess('')} severity="success" sx={{ width: '100%' }}>
          {success}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError('')}
      >
        <Alert onClose={() => setError('')} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CRMDashboard; 